import { inject } from '@angular/core';
import { CanMatchFn, GuardResult, PartialMatchRouteSnapshot, Router } from '@angular/router';
import { Authentification } from './authentification';
import { peutConsulterInstallation, estAdministrateurGlobal } from './droits-installations';
import { TicketService } from './ticket';
import { AccesPages, RefusAccesPage } from './acces-pages';
import { ErreurApi, explicationErreurApi, normaliserErreurHttp } from './api-error';

/** Contrôle le périmètre local avant de charger une page métier ou ses résolveurs. */
export const accesInstallationsGuard: CanMatchFn = (_route, _segments, snapshot) =>
  verifierAcces(snapshot, false);

/** L’administration globale exige ROLE_ADMIN, indépendamment des rôles locaux. */
export const accesAdministrateurGuard: CanMatchFn = (_route, _segments, snapshot) =>
  verifierAcces(snapshot, true);

/** Un refus sélectionne l’écran explicatif du même chemin ; seule une session absente mène à la connexion. */
async function verifierAcces(
  route: PartialMatchRouteSnapshot,
  administrateurSeulement: boolean,
): Promise<GuardResult> {
  const auth = inject(Authentification);
  const router = inject(Router);
  const tickets = inject(TicketService);
  const acces = inject(AccesPages);
  const session = auth.sessionVersion;
  const navigationId = router.currentNavigation()?.id;
  const actif = () =>
    session === auth.sessionVersion && navigationId === router.currentNavigation()?.id;
  const refus = (contexte: RefusAccesPage) => acces.refuser(navigationId, contexte);
  try {
    if (!(await auth.sessionValide()))
      return actif() ? router.createUrlTree(['/connexion']) : false;
    let utilisateur = await auth.getCurrentTokenInfo();
    if (!actif()) return false;
    if (estAdministrateurGlobal(utilisateur)) return true;
    if (administrateurSeulement) return refus({ motif: 'administration' });
    const installation = route.paramMap.get('id');
    if (installation !== null) {
      const installationId = Number(installation);
      if (!Number.isSafeInteger(installationId) || installationId <= 0)
        return refus({ motif: 'introuvable' });
      return (
        peutConsulterInstallation(utilisateur, installationId) ||
        refus({ motif: 'installation', installationId })
      );
    }
    const ticketId = route.paramMap.get('ticketId');
    if (ticketId !== null) {
      if (!peutConsulterInstallation(utilisateur)) return refus({ motif: 'sans-installation' });
      const ticket = await tickets.getTicket(ticketId);
      // Le JWT peut avoir été rafraîchi ou la session remplacée pendant cette lecture.
      utilisateur = await auth.getCurrentTokenInfo();
      if (!actif()) return false;
      if (!ticket) return refus({ motif: 'introuvable' });
      const id = Number(ticket.installation?.id ?? ticket.id_installation);
      if (!Number.isSafeInteger(id) || id <= 0) return refus({ motif: 'indisponible' });
      return (
        peutConsulterInstallation(utilisateur, id) ||
        refus({
          motif: 'installation',
          installationId: id,
          installationNom: ticket.installation?.nom,
        })
      );
    }
    return peutConsulterInstallation(utilisateur) || refus({ motif: 'sans-installation' });
  } catch (erreur) {
    if (!actif()) return false;
    const normalisee = normaliserErreurHttp(erreur);
    // Distingue un refus du serveur d’une panne réseau ou d’une réponse inexploitable.
    const status = normalisee instanceof ErreurApi ? normalisee.status : undefined;
    if (normalisee instanceof ErreurApi && (status === 403 || normalisee.nature === 'metier'))
      return refus({ motif: 'serveur', ...explicationErreurApi(normalisee) });
    return refus({
      motif: status === 404 ? 'introuvable' : 'indisponible',
    });
  }
}
